import StarIcon from "@mui/icons-material/Star";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { ReactNode } from "react";
import { PublicLinkActionType } from "../interfaces/DTO/PublicAction";
import { ContactStatus, ProfileCompletionStage } from "../interfaces/DTO/crm/contact";
import { AssetType, SubAssetType, UserTargetFrequency } from "../interfaces/DTO/grow/userAsset";
import { AgentTaskType } from "../interfaces/DTO/agent/agentPhoneBook";
import { UserRole, UserType } from "../interfaces/DTO/user";
import { SystemDataType } from "../interfaces/DTO/storageFile";
import { PreAdviceTaskType, TaskStatus } from "../interfaces/DTO/tasks/PreadviceTasks";
import { CustomAssetType } from "../interfaces/DTO/grow/DepositRange";
import { ContactPreferenceType } from "../interfaces/DTO/contactPreferences";
import {
  TranscriptFetchStatus,
  ContextSnapshotStatus,
  ContextSnapshotStatusLabels,
  Stage0Status,
  DerivedProcessingStatus,
  TasksDraftStatus,
  Meeting
} from "../interfaces/DTO/meeting/meeting";
import { FormatTranscriptFetchStatus } from "./MeetingsUtil";

export enum EntityFilterCategory {
  All = "all",
  Contacts = "contacts",
  Families = "families",
  HasQuestion = "has_question",
  RequiresAttention = "requires_attention",
  Favorite = "favorite",
  PreAdviceOnly = "pre_advice_only", // יש רק טרום ייעוץ
  Leads = "leads", // אין לא טרום ייעוץ ולא last_data_reported_period
}

export const formatEntityFilterCategory = (category: EntityFilterCategory): string => {
  switch (category) {
    case EntityFilterCategory.All:
      return "הכל";
    case EntityFilterCategory.Contacts:
      return "לקוחות";
    case EntityFilterCategory.Families:
      return "משפחות";
    case EntityFilterCategory.HasQuestion:
      return "לענות";
    case EntityFilterCategory.RequiresAttention:
      return "לטיפול";
    case EntityFilterCategory.Favorite:
      return "מועדפים";
    case EntityFilterCategory.PreAdviceOnly:
      return "טרום ייעוץ בלבד";
    case EntityFilterCategory.Leads:
      return "לידים";
    default:
      return category;
  }
};



export const iconEntityFilterCategory = (category: EntityFilterCategory): JSX.Element => {
  switch (category) {
    case EntityFilterCategory.HasQuestion:
      return <HelpOutlineIcon />;
    case EntityFilterCategory.RequiresAttention:
      return <WarningAmberIcon />;
    case EntityFilterCategory.Contacts:
      return <PersonIcon />;
    case EntityFilterCategory.Families:
      return <GroupsIcon />;
    case EntityFilterCategory.All:
      return <AllInclusiveIcon />;
    case EntityFilterCategory.Favorite:
      return <StarIcon />;
    case EntityFilterCategory.PreAdviceOnly:
      return <AssignmentIcon />;
    case EntityFilterCategory.Leads:
      return <PersonAddIcon />;
    default:
      return null;
  }
};

export const colorEntityFilterCategory = (category: EntityFilterCategory): string => {
  switch (category) {
    case EntityFilterCategory.HasQuestion:
      return "#0288D1"; // כחול בהיר
    case EntityFilterCategory.RequiresAttention:
      return "#F57C00"; // כתום
    case EntityFilterCategory.Contacts:
      return "#2E7D32"; // ירוק
    case EntityFilterCategory.Families:
      return "#6A1B9A"; // סגול
    case EntityFilterCategory.Favorite:
      return "#FFD700"; // זהב
    case EntityFilterCategory.PreAdviceOnly:
      return "#03A9F4"; // תכלת
    case EntityFilterCategory.Leads:
      return "#FF5722"; // כתום-אדום
    case EntityFilterCategory.All:
    default:
      return "#9E9E9E"; // אפור
  }
};

export function buildPublicTokenUrl(token: string): string {
  return `${window.location.origin}/public/${token}`;
}
export function buildRelativePublicTokenPath(token: string): string {
  return `/public/${token}`;
}

export function formatPublicLinkPurpose(purpose: PublicLinkActionType | string): string {
  switch (purpose) {
    case PublicLinkActionType.UPDATE_CONTACT:
      return "עדכון פרטי קשר";
    case PublicLinkActionType.UPLOAD_DOCUMENT:
      return "העלאת מסמכים";
    case PublicLinkActionType.SIGN_CONSENT:
      return "חתימה על הסכמה";
    default:
      return "לא ידוע";
  }
}

export const formatContactStatus = (status: ContactStatus): string => {
  switch (status) {
    case ContactStatus.NEW:
      return "חדש";
    case ContactStatus.ENGAGED:
      return "במגע";
    case ContactStatus.VALIDATED:
      return "מאומת";
    case ContactStatus.DATA_FETCHED:
      return "נתונים נשלפו";
    case ContactStatus.PAYING_CLIENT:
      return "לקוח משלם";
    case ContactStatus.ACTIVE_SUBSCRIPTION:
      return "מנוי פעיל";
    case ContactStatus.DORMANT:
      return "לא פעיל";
    case ContactStatus.NOT_INTERESTED:
      return "לא מעוניין";
    case ContactStatus.CHURNED:
      return "עזב.ה";
    case ContactStatus.EXEMPT_FROM_PAYMENT:
      return "לא לתשלום";
    default:
      return status;
  }
};

export const formatProfileCompletionStage = (stage: ProfileCompletionStage): string => {
  switch (stage) {
    case ProfileCompletionStage.REGISTERED:
      return "נרשם בלבד";
    case ProfileCompletionStage.BASIC_INFO:
      return "פרטים בסיסיים";
    case ProfileCompletionStage.EMPLOYMENT:
      return "תעסוקה";
    case ProfileCompletionStage.DOCUMENTS:
      return "מסמכים";
    case ProfileCompletionStage.VERIFICATION:
      return "אימות";
    case ProfileCompletionStage.COMPLETE:
      return "הושלם";
    default:
      return stage;
  }
};
export const formatUserTargetFrequency = (freq: UserTargetFrequency): string => {
  switch (freq) {
    case UserTargetFrequency.MONTHLY:
      return "חודשי";
    case UserTargetFrequency.YEARLY:
      return "שנתי";
    case UserTargetFrequency.ONETIME:
      return "חד פעמי";
    default:
      return "לא ידוע";
  }
};
export function formatSubAssetType(type?: SubAssetType | null): string {
  switch (type) {
    case SubAssetType.INVESTMENT_PORTFOLIO:
      return "תיק השקעות";
    case SubAssetType.STOCKS:
      return "מניות";
    case SubAssetType.OPTIONS:
      return "אופציות";
    default:
      return "—";
  }
}
export function formatCustomAssetType(type: CustomAssetType): string {
  switch (type) {
    case CustomAssetType.FreeIncome:
      return "הכנסה פנויה";
    case CustomAssetType.Portfolio:
      return "תיק השקעות";
    case CustomAssetType.Loan:
      return "הלוואה";
    case CustomAssetType.Mortgage:
      return "משכנתא";
    case CustomAssetType.Withdrawal:
      return "משיכה";
    case CustomAssetType.RSU:
      return "RSU";
    case CustomAssetType.Other:
      return "אחר";
    default:
      return type;
  }
}
export function colorCustomAssetType(type: CustomAssetType): string {
  switch (type) {
    case CustomAssetType.FreeIncome:
      return "#4caf50"; // ירוק
    case CustomAssetType.Portfolio:
      return "#2196f3"; // כחול
    case CustomAssetType.Loan:
      return "#ff9800"; // כתום
    case CustomAssetType.Mortgage:
      return "#9c27b0"; // סגול
    case CustomAssetType.Withdrawal:
      return "#f44336"; // אדום
    case CustomAssetType.RSU:
      return "#607d8b"; // אפור כהה
    case CustomAssetType.Other:
      return "#795548"; // חום
    default:
      return "#90a4ae"; // ברירת מחדל
  }
}

export const colorAssetType = (type: AssetType): string => {
  switch (type) {
    case AssetType.CASH:
      return "#388E3C"; // ירוק כהה אך נעים
    case AssetType.MARKETABLE:
      return "#303F9F"; // כחול כהה נעים
    case AssetType.NON_MARKETABLE:
      return "#8E24AA"; // סגול כהה נעים
    case AssetType.OTHER:
    default:
      return "#616161"; // אפור כהה נעים
  }
};
export const formatAgentTaskType = (type: AgentTaskType): string => {
  switch (type) {
    case AgentTaskType.TRANSFER_IN:
      return "ניוד נכנס";
    case AgentTaskType.TRANSFER_OUT:
      return "ניוד יוצא";
    case AgentTaskType.FEE_CHANGE:
      return "שינוי דמי ניהול";
    case AgentTaskType.NEW_CLIENT:
      return "לקוח חדש";
    case AgentTaskType.JOIN_POLICY:
      return "הצטרפות לביטוח";
    case AgentTaskType.OTHER:
      return "אחר";
    case AgentTaskType.JOIN_REJECT:
      return "ריג'קט בהצטרפות";
    case AgentTaskType.DEPOSIT_TRACKING:
      return "מעקב הפקדות";
    case AgentTaskType.WITHDRAWAL:
      return "פדיון/משיכה";
    case AgentTaskType.PATH_CHANGE:
      return "שינוי מסלול";
    case AgentTaskType.LOAN_TRACKING:
      return "מעקב הלוואה";
    case AgentTaskType.CONSULTING:
      return "ייעוץ פנסיוני";
    case AgentTaskType.POWER_OF_ATTORNEY:
      return "ייפוי כוח";
    case AgentTaskType.VIEW_POWER_OF_ATTORNEY:
      return "ייפוי כוח לצפייה";
    case AgentTaskType.AGENT_UPDATE:
      return "עדכון סוכן";
    case AgentTaskType.LOST_ACCOUNT_TRACKING:
      return "חשבונות מנותקי קשר";
    case AgentTaskType.AGENT_APPOINTMENT:
      return "מינוי סוכן";
    case AgentTaskType.AGENT_CANCELLATION:
      return "ביטול מינוי סוכן";
    case AgentTaskType.COVERAGE_REQUEST:
      return "בקשת כיסויים ביטוחיים";
    case AgentTaskType.COVERAGES_RESPONSE:
      return "קבלת כיסויים ביטוחיים";
    default:
      return type;
  }
};
export const colorAgentTaskType = (type: AgentTaskType): string => {
  switch (type) {
    case AgentTaskType.TRANSFER_IN:
      return "#1976d2"; // כחול
    case AgentTaskType.TRANSFER_OUT:
      return "#1565c0"; // כחול כהה יותר
    case AgentTaskType.FEE_CHANGE:
      return "#6a1b9a"; // סגול כהה
    case AgentTaskType.NEW_CLIENT:
      return "#2e7d32"; // ירוק כהה
    case AgentTaskType.JOIN_POLICY:
      return "#388e3c"; // ירוק
    case AgentTaskType.OTHER:
      return "#757575"; // אפור
    case AgentTaskType.JOIN_REJECT:
      return "#d32f2f"; // אדום
    case AgentTaskType.DEPOSIT_TRACKING:
      return "#0288d1"; // תכלת
    case AgentTaskType.WITHDRAWAL:
      return "#c62828"; // אדום כהה
    case AgentTaskType.PATH_CHANGE:
      return "#8e24aa"; // סגול
    case AgentTaskType.LOAN_TRACKING:
      return "#00897b"; // טורקיז כהה
    case AgentTaskType.CONSULTING:
      return "#fbc02d"; // צהוב
    case AgentTaskType.POWER_OF_ATTORNEY:
      return "#f57c00"; // כתום כהה
    case AgentTaskType.VIEW_POWER_OF_ATTORNEY:
      return "#5c6bc0"; // אינדיגו
    case AgentTaskType.AGENT_UPDATE:
      return "#ef6c00"; // כתום
    case AgentTaskType.LOST_ACCOUNT_TRACKING:
      return "#b71c1c"; // בורדו כהה
    case AgentTaskType.AGENT_APPOINTMENT:
      return "#43a047"; // ירוק בהיר
    case AgentTaskType.AGENT_CANCELLATION:
      return "#e53935"; // אדום בהיר
    case AgentTaskType.COVERAGE_REQUEST:
      return "#0277bd"; // כחול ביטוח
    case AgentTaskType.COVERAGES_RESPONSE:
      return "#00838f"; // ציאן כהה
    default:
      return "#9e9e9e"; // אפור ברירת מחדל
  }
};
export const formatSystemDataType = (value: string): string => {
  switch (value) {
    case SystemDataType.BANK_PERMISSION:
      return "הרשאה למשיכה מהבנק";
    case SystemDataType.ID_DOCUMENT:
      return "תעודת זהות";
    case SystemDataType.HAR_HABITUACH:
      return "הר הביטוח";
    case SystemDataType.PORTABILITY_REQUEST:
      return "בקשה לניוד";
    case SystemDataType.MEETING_SUMMARY:
      return "סיכום פגישה";
    case SystemDataType.PRE_ADVICE_SYSTEM:
      return "טרום ייעוץ";
    case SystemDataType.PUBLIC_ACTION_CONSENT:
      return "אישור צפיה בפוליסות";
    case SystemDataType.VIEW_POLICIES_CONSENT:
      return "ייפוי כוח לצפייה בפוליסות";
    case SystemDataType.MORTGAGE_REPORT:
      return "דוח משכנתא";
    case SystemDataType.INSURANCE_COVERAGE_REPORT:
      return "דוח כיסויים ביטוחיים";
    case SystemDataType.SALARY_SLIP:
      return "תלוש שכר";
    case SystemDataType.OTHER:
      return "אחר";
    default:
      return value;
  }
};

export const formatUserRole = (role: UserRole): string => {
  switch (role) {
    case UserRole.SUPERADMIN:
      return "מנהל-על";
    case UserRole.COMPANY_ADMIN:
      return "מנהל חברה";
    case UserRole.USER:
      return "משתמש";
    default:
      return role;
  }
};
export const formatUserType = (type: UserType): string => {
  switch (type) {
    case UserType.CLIENT:
      return "לקוח";
    case UserType.AGENT:
      return "סוכן";
    case UserType.OPERATOR:
      return "מוקדן";
    case UserType.LEAD:
      return "ליד";
    case UserType.DEMO:
      return "דמו";
    default:
      return type;
  }
};

export function formatTaskStatus(status: TaskStatus): string {
  switch (status) {
    case TaskStatus.PENDING:
      return "ממתין לביצוע";
    case TaskStatus.IN_PROGRESS:
      return "בתהליך";
    case TaskStatus.REQUEST_SENT:
      return "בקשה נשלחה";
    case TaskStatus.WAITING_FOR_CLIENT:
      return "ממתין ללקוח";
    case TaskStatus.COMPLETED:
      return "הושלם";
    case TaskStatus.FAILED:
      return "נכשל";
    case TaskStatus.FILE_SAVED:
      return "קובץ נשמר";
    case TaskStatus.PERMANENT_FAILURE:
      return "נכשל סופית";
    case TaskStatus.CANCELLED:
      return "בוטל";
    case TaskStatus.IDLE:
      return "סרק";
    default:
      return status;
  }
}

export function colorTaskStatus(status: TaskStatus): string {
  switch (status) {
    case TaskStatus.PENDING:
      return "#9e9e9e"; // אפור בינוני
    case TaskStatus.IN_PROGRESS:
      return "#1976d2"; // כחול (Primary)
    case TaskStatus.REQUEST_SENT:
      return "#0288d1"; // כחול בהיר
    case TaskStatus.WAITING_FOR_CLIENT:
      return "#fbc02d"; // צהוב זהיר
    case TaskStatus.COMPLETED:
      return "#2e7d32"; // ירוק מוצלח
    case TaskStatus.FAILED:
      return "#d32f2f"; // אדום חזק
    case TaskStatus.CANCELLED:
      return "#6d4c41"; // חום כהה
    case TaskStatus.IDLE:
      return "#bdbdbd"; // אפור בהיר
    default:
      return "#e0e0e0"; // אפור כללי
  }
}
export function chipColorTaskStatus(status: TaskStatus): "default" | "primary" | "warning" | "success" | "error" {
  switch (status) {
    case TaskStatus.COMPLETED:
      return "success";
    case TaskStatus.FAILED:
    case TaskStatus.PERMANENT_FAILURE:
      return "error";
    case TaskStatus.IN_PROGRESS:
    case TaskStatus.REQUEST_SENT:
    case TaskStatus.FILE_SAVED:
      return "primary";
    case TaskStatus.WAITING_FOR_CLIENT:
      return "warning";
    default:
      return "default";
  }
}

 // קבלת תיאור סוג המשימה
  export const formatPreAdviceTaskType = (taskType: PreAdviceTaskType) => {
    switch (taskType) {
      case PreAdviceTaskType.PRE_ADVICE_FORM_SIGNING:
        return "חתימת טופס טרום ייעוץ";
      case PreAdviceTaskType.PRE_ADVICE_MISLAKA:
        return "טרום ייעוץ מסלקה";
      case PreAdviceTaskType.PRE_ADVICE_FORM_AND_MISLAKA:
        return "חתימת טופס + טרום ייעוץ מסלקה";
      case PreAdviceTaskType.HAR_BITUACH_DOCUMENTS:
        return "מסמכי הר ביטוח";
      case PreAdviceTaskType.HAR_BITUACH_FORM_SIGNING:
        return "חתימת טופס הר ביטוח";
      case PreAdviceTaskType.HAR_BITUACH_FORM_AND_DOCUMENTS:
        return "חתימת טופס + מסמכי הר ביטוח";
      case PreAdviceTaskType.POWER_OF_ATTORNEY:
        return "ייפוי כוח";
      default:
        return taskType;
    }
  };

export const formatPreferenceType = (preferenceType: ContactPreferenceType): string => {
  switch (preferenceType) {
    case ContactPreferenceType.CAPITAL_DISTRIBUTION:
      return "פיזור הוני";
    case ContactPreferenceType.EDUCATION_FUND_DISTRIBUTION:
      return "פיזור קרן השתלמות";
    case ContactPreferenceType.PENSION_DISTRIBUTION:
      return "פיזור פנסיה";
    default:
      return preferenceType;
  }
}

// Extended formatter to include newer purposes without touching the legacy switch above
export function formatPublicLinkActionType(purpose: PublicLinkActionType | string): string {
  if (purpose === PublicLinkActionType.VIEW_P_CONSENT || purpose === "view_policies_consent") {
    return "אישור הרשאה לצפייה בפוליסות";
  }
  return formatPublicLinkPurpose(purpose as any);
}

/**
 * פונקציה לפורמט סטטוס משיכת טרנסקריפט
 * @param status - סטטוס משיכת הטרנסקריפט
 * @returns מחרוזת מפורמטת של הסטטוס
 */
export const formatTranscriptFetchStatus = (status: TranscriptFetchStatus | null | undefined): string => {
  if (!status) return "";
  return FormatTranscriptFetchStatus(status);
};

/**
 * פונקציה לפורמט סטטוס יצירת Context Snapshot
 * @param status - סטטוס יצירת ה-Snapshot
 * @returns מחרוזת מפורמטת של הסטטוס
 */
export const formatContextSnapshotStatus = (status: ContextSnapshotStatus | null | undefined): string => {
  if (!status) return "";
  return ContextSnapshotStatusLabels[status] || status;
};

/**
 * פונקציה לקבלת סטטוס כולל של עיבוד AI
 * מחזירה את השלב שרץ **עכשיו** או הכשלון הראשון
 * סדר עדיפות: PROCESSING > FAILED > PENDING > COMPLETED
 */
export const getOverallAIProcessingStatus = (meeting: Meeting): {
  status: string;
  label: string;
  color: "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success";
} => {
  const { stage0_status, derived_processing_status, tasks_draft_status } = meeting;

  // אם אין שום סטטוס
  if (!stage0_status && !derived_processing_status && !tasks_draft_status) {
    return { status: "NOT_STARTED", label: "טרם התחיל", color: "default" };
  }

  // ========================================
  // עדיפות 1: משהו רץ **עכשיו** (PROCESSING)
  // ========================================

  // אם Tasks רץ עכשיו - זה השלב הכי מתקדם
  if (tasks_draft_status === TasksDraftStatus.PROCESSING) {
    return { status: "TASKS_PROCESSING", label: "Tasks רץ", color: "info" };
  }

  // אם Derived רץ עכשיו
  if (derived_processing_status === DerivedProcessingStatus.PROCESSING) {
    return { status: "DERIVED_PROCESSING", label: "Derived רץ", color: "info" };
  }

  // אם Stage 0 רץ עכשיו
  if (stage0_status === Stage0Status.PROCESSING) {
    return { status: "STAGE0_PROCESSING", label: "Stage 0 רץ", color: "info" };
  }

  // ========================================
  // עדיפות 2: כשלון (FAILED)
  // ========================================

  // אם Tasks נכשל
  if (tasks_draft_status === TasksDraftStatus.FAILED) {
    return { status: "TASKS_FAILED", label: "Tasks נכשל", color: "error" };
  }

  // אם Derived נכשל
  if (derived_processing_status === DerivedProcessingStatus.FAILED) {
    return { status: "DERIVED_FAILED", label: "Derived נכשל", color: "error" };
  }

  // אם Stage 0 נכשל
  if (stage0_status === Stage0Status.FAILED) {
    return { status: "STAGE0_FAILED", label: "Stage 0 נכשל", color: "error" };
  }

  // ========================================
  // עדיפות 3: ממתין (PENDING)
  // ========================================

  // אם Tasks ממתין
  if (tasks_draft_status === TasksDraftStatus.PENDING) {
    return { status: "TASKS_PENDING", label: "Tasks ממתין", color: "warning" };
  }

  // אם Derived ממתין
  if (derived_processing_status === DerivedProcessingStatus.PENDING) {
    return { status: "DERIVED_PENDING", label: "Derived ממתין", color: "warning" };
  }

  // אם Stage 0 ממתין
  if (stage0_status === Stage0Status.PENDING) {
    return { status: "STAGE0_PENDING", label: "Stage 0 ממתין", color: "warning" };
  }

  // ========================================
  // עדיפות 4: הושלם (COMPLETED)
  // מחזיר את השלב הכי מתקדם שהושלם
  // ========================================

  // אם כל השלבים הושלמו
  if (
    stage0_status === Stage0Status.COMPLETED &&
    derived_processing_status === DerivedProcessingStatus.COMPLETED &&
    tasks_draft_status === TasksDraftStatus.COMPLETED
  ) {
    return { status: "ALL_COMPLETED", label: "הושלם", color: "success" };
  }

  // אם Tasks הושלם (השלב הכי מתקדם)
  if (tasks_draft_status === TasksDraftStatus.COMPLETED) {
    return { status: "TASKS_COMPLETED", label: "Tasks הושלם", color: "success" };
  }

  // אם Derived הושלם
  if (derived_processing_status === DerivedProcessingStatus.COMPLETED) {
    return { status: "DERIVED_COMPLETED", label: "Derived הושלם", color: "success" };
  }

  // אם רק Stage 0 הושלם (ושאר השלבים NULL)
  if (stage0_status === Stage0Status.COMPLETED) {
    return { status: "STAGE0_COMPLETED", label: "Stage 0 הושלם", color: "success" };
  }

  return { status: "UNKNOWN", label: "לא ידוע", color: "default" };
};
